import { isMarketingDocument } from "@/routing/marketingDocument";

/**
 * Records where the visitor came from in two cookies the backend reads at
 * registration: `ws_src_first` (never overwritten) and `ws_src_last`. Only a
 * landing writes: a URL with a tracking parameter, or a referrer outside
 * webshare.io. Marketing pages wait for CookieYes to allow analytics; the
 * dashboard has no consent banner and writes straight away.
 */

const TRACKING_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "irclid",
  "referral_code",
  "sub_id",
  "subid",
];

const OWN_HOST = /(^|\.)webshare\.io$/;
const DEV_HOST = /(^|\.)dev\.webshare\.io$/;
// Coming back from Google sign-in or a payment is not a new landing.
const RETURN_TRIP_HOST = /^accounts\.google\.com$|(^|\.)(stripe|paypal)\.com$/;
// Nor is following a link in an account email: the referrer there is the
// visitor's mailbox (temp-mail.org, the Gmail app), not where they came from.
const ACCOUNT_EMAIL_PATH =
  /^\/(activation|resetpassword|profile\/changeemail)(\/|$)/;

const MAX_PARAM_LENGTH = 200;
const MAX_URL_LENGTH = 300;
const MAX_COOKIE_LENGTH = 1500;
const MAX_AGE = 90 * 24 * 60 * 60;

type Fields = Record<string, string>;

function externalReferrer(): string | undefined {
  if (ACCOUNT_EMAIL_PATH.test(location.pathname)) return undefined;
  try {
    const { hostname } = new URL(document.referrer);
    if (!OWN_HOST.test(hostname) && !RETURN_TRIP_HOST.test(hostname)) {
      return document.referrer.slice(0, MAX_URL_LENGTH);
    }
  } catch {
    // No referrer.
  }
  return undefined;
}

function landingFields(): Fields | undefined {
  const params = new URLSearchParams(location.search);
  const fields: Fields = {};
  TRACKING_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) fields[key] = value.slice(0, MAX_PARAM_LENGTH);
  });

  const referrer = externalReferrer();
  if (Object.keys(fields).length === 0 && !referrer) return undefined;

  fields.landing = (location.origin + location.pathname).slice(
    0,
    MAX_URL_LENGTH
  );
  if (referrer) fields.referrer = referrer;
  return fields;
}

/** Drops the longest field until the cookie value fits. */
function encode(fields: Fields, ts: number): string {
  const value = encodeURIComponent(JSON.stringify({ ...fields, ts }));
  if (value.length <= MAX_COOKIE_LENGTH) return value;

  const longest = Object.keys(fields).reduce((a, b) =>
    fields[a].length >= fields[b].length ? a : b
  );
  const rest = { ...fields };
  delete rest[longest];
  return encode(rest, ts);
}

function cookieAttributes(): string {
  const host = location.hostname;
  // Dev never writes into production's cookies; localhost stays host-only.
  const domain = DEV_HOST.test(host)
    ? "; Domain=.dev.webshare.io"
    : OWN_HOST.test(host)
      ? "; Domain=.webshare.io"
      : "";
  return `${domain}; Path=/; Max-Age=${MAX_AGE}; SameSite=Lax; Secure`;
}

function hasCookie(name: string): boolean {
  return new RegExp(`(^|;\\s*)${name}=`).test(document.cookie);
}

function analyticsAllowed(): boolean {
  return /(^|;\s*)cookieyes-consent=[^;]*analytics:yes/.test(document.cookie);
}

export function recordAcquisitionSource(): void {
  try {
    const fields = landingFields();
    if (!fields) return;

    const cookie = encode(fields, Date.now()) + cookieAttributes();
    const write = () => {
      if (!hasCookie("ws_src_first"))
        document.cookie = `ws_src_first=${cookie}`;
      document.cookie = `ws_src_last=${cookie}`;
    };

    if (!isMarketingDocument() || analyticsAllowed()) {
      write();
      return;
    }
    document.addEventListener("cookieyes_consent_update", (event) => {
      const { detail } = event as CustomEvent<{ accepted?: string[] }>;
      if (detail?.accepted?.includes("analytics")) write();
    });
  } catch {
    // Tracking must never stop the page from booting.
  }
}
